import { Dispatch, SetStateAction, useContext, useState } from 'react'
import { useParams } from 'react-router'
import { Close, DeleteOutlined } from '@mui/icons-material'
import { Box, Button, Grid, IconButton } from '@mui/material'
import { GridColumnHeaderTitle, GridRowId, Toolbar, useGridApiContext } from '@mui/x-data-grid-pro'
import { useSnackbar } from 'notistack'

import { useHost } from '../../../../providers/app-provider'
import Api from '../../../../utils/api'
import useSharedStyles from '../../../../utils/shared.styles'
import AccountsContext from '../../context'
import useAccountStyles from '../../index.styles'

import DeleteModal from './delete-modal'
import useStyles from './index.styles'
import RemoveModal from './remove-modal'

interface IProps {
  users: IUserType[]
  selectedIds: GridRowId[]
  setIsDeleting: Dispatch<SetStateAction<boolean>>
  getFolderUsers: () => Promise<void>
}

const PeopleTableToolbar = ({ users, selectedIds, setIsDeleting, getFolderUsers }: IProps) => {
  const classes = useStyles
  const accountClasses = useAccountStyles
  const sharedClasses = useSharedStyles
  const apiRef = useGridApiContext()
  const { accountId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const { setFolderRefreshCounter, isScrolled } = useContext(AccountsContext)
  const host = useHost()

  const selectedUsers = users.filter((user) => selectedIds.includes(user.id))
  const usersToRemove = selectedUsers.filter((user) => user.folders.length > 1)
  const usersToDelete = selectedUsers.filter((user) => user.folders.length === 1)

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [removeModalOpen, setRemoveModalOpen] = useState(false)
  const [chainModals, setChainModals] = useState(false)

  const handleRemoveBtnClick = () => {
    // users with only 1 assigned folder will be deleted
    // for users that have multiple folders assigned we will remove currently folder from their user object
    // if users with both 1 and multiple folders are selected at the same time we will show 2 dialogs - one after the other to remove and delete
    const allHaveOneFolderOnly = selectedUsers.every((user) => user.folders.length === 1)
    const allHaveMultipleFoldersOnly = selectedUsers.every((user) => user.folders.length > 1)

    if (allHaveOneFolderOnly) {
      setDeleteModalOpen(true)
    } else if (allHaveMultipleFoldersOnly) {
      setRemoveModalOpen(true)
    } else {
      setChainModals(true)
      setRemoveModalOpen(true)
    }
  }

  const deleteUsers = async () => {
    setIsDeleting(true)
    setDeleteModalOpen(false)

    const deleteRequests = chainModals
      ? usersToDelete.map((user) => Api.del(`${host}/api/users/${user.id}`))
      : selectedIds.map((id) => Api.del(`${host}/api/users/${id}`))

    await Promise.all(deleteRequests)
      .then((responses) => {
        const failures = responses.filter((response) => !response.ok).length
        if (failures > 0) {
          enqueueSnackbar('One or more users could not be deleted', { variant: 'error' })
        } else {
          enqueueSnackbar('Successfully deleted')
          setFolderRefreshCounter?.((c) => c + 1)
          if (chainModals) {
            setChainModals(false)
          }
        }
        if (failures < responses.length) getFolderUsers()
      })
      .finally(() => {
        setIsDeleting(false)
      })
  }

  const removeUsers = async () => {
    setIsDeleting(true)
    setRemoveModalOpen(false)

    const updateRequests = chainModals
      ? usersToRemove.map((user) => Api.put(`${host}/api/users/${user.id}`, { folders: user.folders.filter((el) => el !== Number(accountId)) }))
      : selectedUsers.map((user) => Api.put(`${host}/api/users/${user.id}`, { folders: user.folders.filter((el) => el !== Number(accountId)) }))

    await Promise.all(updateRequests)
      .then((responses) => {
        const failures = responses.filter((response) => !response.ok).length
        if (failures > 0) {
          enqueueSnackbar('One or more users could not be removed from this account', { variant: 'error' })
        } else {
          enqueueSnackbar('Successfully removed')
          setFolderRefreshCounter?.((c) => c + 1)
          if (chainModals) {
            setDeleteModalOpen(true)
          }
        }
        if (failures < responses.length) getFolderUsers()
      })
      .finally(() => {
        setIsDeleting(false)
      })
  }

  return (
    <>
      <Toolbar style={{ ...accountClasses.gridToolbar, position: isScrolled ? 'fixed' : 'static' }}>
        <Box sx={(theme) => ({ ...accountClasses.gridToolbarContainer(theme), ...(isScrolled ? { borderRadius: 0 } : {}) })}>
          <Grid size={12} container={true} sx={[sharedClasses.centerContentVertical, { justifyContent: 'space-between' }]}>
            <Grid container={true} size={6} sx={sharedClasses.centerContentVertical}>
              <IconButton onClick={() => apiRef.current?.selectRows([], false, true)} aria-label="Cancel selection">
                <Close />
              </IconButton>

              <div style={classes.gridToolbarTitleContainer}>
                <GridColumnHeaderTitle columnWidth={0} label={`${selectedIds.length} selected${selectedIds.length > 4 ? ' (max allowed)' : ''}`} />
              </div>
            </Grid>

            <Grid container={true} size={6} sx={[sharedClasses.centerContentVertical, { justifyContent: 'flex-end' }]}>
              <Button variant="contained" size="small" color="error" startIcon={<DeleteOutlined />} onClick={handleRemoveBtnClick}>
                Remove
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Toolbar>
      <DeleteModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        selectedUsers={chainModals ? usersToDelete : selectedUsers}
        onDelete={deleteUsers}
        chainModals={chainModals}
        setChainModals={setChainModals}
      />
      <RemoveModal
        open={removeModalOpen}
        setOpen={setRemoveModalOpen}
        selectedUsers={chainModals ? usersToRemove : selectedUsers}
        onRemove={removeUsers}
        chainModals={chainModals}
        setChainModals={setChainModals}
      />
    </>
  )
}

export default PeopleTableToolbar
